/*
 * cart-upsell.js — B (A/B-Test, Task #055)
 * Zeigt EIN kuratiertes Complementary-Produkt im Cart-Drawer mit 10% (via _upsell-Function).
 * Auswahl bei mehreren Cart-Items:
 *   1) Kandidat, der im Complementary-Feld von >=2 Cart-Items vorkommt (bester Konsens, hoechster Count).
 *   2) Kein Konsens -> Fallback auf das ERSTE Cart-Item (oben im Drawer).
 * Bereits im Warenkorb liegende Produkte werden ausgeschlossen. 1 Upsell pro Drawer.
 * Quelle: /recommendations/products.json?intent=complementary (= verifiziert 100% kuratiertes Metafield).
 * Sprache und Waehrung (17.09.2026, Checkout-Analyse Kliesch): Texte je Shopify.locale, Abrufe mit
 * Sprachpraefix (Shopify.routes.root, sonst Titel und Links immer deutsch), Preise im Format der
 * Kundenwaehrung aus data-money-sample ({{ 123456 | money }}). Vorher stand vor Dollarbetraegen ein Euro-Zeichen.
 * Kurzinfo (02.10.2026, Vladi): „Was ist das?“ zeigt die ersten zwei Stichpunkte der Produktbeschreibung
 * (alle aktiven Produkte beginnen mit <ul><li><strong>…). Die Beschreibung steckt schon im
 * Empfehlungs-JSON, also kein zusaetzlicher Abruf. Am Computer genuegt Ueberfahren der Karte, am Handy Tippen.
 * Ueberschrift seit 02.10.2026 in der Sprache des Kastens („Kombi-Preis“ statt „reduziert“, Strategie 30.09.).
 */
const CART_UPSELL_TEXTE = {
  de: { heading: 'Passt dazu – im Kombi-Preis:', add: '+ Dazu', addLabel: '{titel} hinzufügen', done: '✓ Passend dazu hinzugefügt', info: 'Was ist das?' },
  en: { heading: 'Goes well with it – bundle price:', add: '+ Add', addLabel: 'Add {titel}', done: '✓ Matching item added', info: 'What is it?' },
  fr: { heading: 'À associer – prix combiné :', add: '+ Ajouter', addLabel: 'Ajouter {titel}', done: '✓ Article assorti ajouté', info: 'C’est quoi ?' },
  it: { heading: 'Da abbinare – prezzo combinato:', add: '+ Aggiungi', addLabel: 'Aggiungi {titel}', done: '✓ Articolo abbinato aggiunto', info: 'Cos’è?' },
  es: { heading: 'Para acompañar – precio combinado:', add: '+ Añadir', addLabel: 'Añadir {titel}', done: '✓ Artículo a juego añadido', info: '¿Qué es?' },
};

class CartUpsell extends HTMLElement {
  connectedCallback() {
    // Kurzinfo: Dokument-Listener je Element an- und in disconnectedCallback wieder abmelden
    // (renderContents ersetzt die Schublade samt Element).
    this._schliessen = this._schliessen || ((e) => this.schliessenAussen(e));
    document.addEventListener('click', this._schliessen);
    if (this._init) return;
    this._init = true;
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart-upsell__add');
      if (btn) { e.preventDefault(); this.addToCart(btn.dataset.variantId, btn); return; }
      const info = e.target.closest('.cart-upsell__info-link');
      if (info) {
        e.preventDefault();
        const card = info.closest('.cart-upsell__card');
        const offen = card.classList.toggle('is-open');
        info.setAttribute('aria-expanded', String(offen));
      }
    });
    this.render();
  }

  disconnectedCallback() {
    if (this._schliessen) document.removeEventListener('click', this._schliessen);
  }

  // Offene Kurzinfo schliesst bei jedem Tippen ausserhalb des Links (auch auf das Fenster selbst),
  // sonst verdeckt sie am Handy die Warenkorb-Zeile darueber.
  schliessenAussen(e) {
    if (e.target.closest && e.target.closest('.cart-upsell__info-link')) return;
    this.querySelectorAll('.cart-upsell__card.is-open').forEach((c) => {
      c.classList.remove('is-open');
      const l = c.querySelector('.cart-upsell__info-link'); if (l) l.setAttribute('aria-expanded', 'false');
    });
  }

  get root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  text(key, titel) {
    const lang = String((window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
    const t = CART_UPSELL_TEXTE[lang] || CART_UPSELL_TEXTE.en;
    return t[key].replace('{titel}', titel || '');
  }

  get cartProductIds() { return (this.dataset.cartProductIds || '').split(',').filter(Boolean); }
  get cartVariantIds() { return (this.dataset.cartVariantIds || '').split(',').filter(Boolean); }
  get excludeProductIds() { return (this.dataset.excludeProductIds || '').split(',').filter(Boolean); }
  // Zielpreise je Produkt-ID in Cent, aus dem Metafeld custom.upsell_price.
  // Liegt fuer ein Produkt keiner vor, gilt der Grundsatz von 10 Prozent.
  get upsellPrices() {
    try { return JSON.parse(this.dataset.upsellPrices || '{}'); } catch { return {}; }
  }

  async render() {
    // Ein Upsell pro Drawer: sobald ein Upsell-Item im Warenkorb liegt, ruht der Slot.
    // Hoechstens zwei Fragen pro Kunde: Stufe 1 (Bundle-Variante) oder Stufe 2 (Produktseite)
    // schon genommen -> der Drawer haelt still. Sonst fuehlt sich der Kunde bedraengt.
    if (this.dataset.stufe1Genommen === 'true' || this.dataset.stufe2Genommen === 'true') {
      this.hidden = true;
      return;
    }
    if (this.dataset.hasUpsell === 'true') {
      this.innerHTML =
        `<div class="cart-upsell__inner"><p class="cart-upsell__heading cart-upsell__done">${this.esc(this.text('done'))}</p></div>`;
      this.hidden = false;
      return;
    }
    const cartPids = [...new Set(this.cartProductIds)]; // distinct, Reihenfolge erhalten (erstes = oben)
    if (!cartPids.length) { this.hidden = true; return; }
    try {
      const pick = await this.pickSuggestion(cartPids);
      if (!pick) { this.hidden = true; return; }
      this.innerHTML = this.cardHtml(pick);
      this.hidden = false;
    } catch (e) {
      this.hidden = true;
    }
  }

  async fetchComplementary(productId) {
    try {
      const r = await fetch(`${this.root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&intent=complementary&limit=10`);
      if (!r.ok) return [];
      return (await r.json()).products || [];
    } catch { return []; }
  }

  async fetchByHandle(handle) {
    try {
      const r = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`);
      if (!r.ok) return null;
      return await r.json();
    } catch { return null; }
  }

  // waehlt das beste Add-on; gibt {product, variant} oder null
  async pickSuggestion(cartPids) {
    const inCartP = new Set(this.cartProductIds.map(String));
    const inCartV = new Set(this.cartVariantIds.map(String));
    const excluded = new Set(this.excludeProductIds.map(String)); // BOM-Exclusion: Set-Bestandteile der Cart-Varianten
    const valid = (p) => {
      if (!p || inCartP.has(String(p.id)) || excluded.has(String(p.id))) return null;
      const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
      if (!v || v.available === false || inCartV.has(String(v.id))) return null;
      return v;
    };

    // Varianten-Override: kuratierter Vorschlag der ersten Cart-Variante mit custom.drawer_override
    const ovHandle = (this.dataset.overrideHandle || '').trim();
    if (ovHandle) {
      const ov = await this.fetchByHandle(ovHandle);
      if (ov) {
        ov.url = ov.url || this.root + 'products/' + ov.handle;
        const v = valid(ov);
        if (v) return { product: ov, variant: v };
      }
    }

    const lists = await Promise.all(cartPids.map((pid) => this.fetchComplementary(pid)));

    // Stufe 2 (Produktseite) zeigt bereits den ERSTEN gueltigen Complementary-Eintrag desselben
    // Produkts -- seit 21.08.2026 aus derselben kuratierten Liste wie hier. Ohne diese Sperre
    // stellt der Drawer Sekunden spaeter dieselbe Frage noch einmal. Gemessen am 19.08.2026:
    // 140 solcher Doppel-Anzeigen, 0 Kaeufe (erwartet waeren 3,6 gewesen).
    const schonGezeigt = new Set();
    for (const list of lists) {
      for (const p of list) {
        if (valid(p)) { schonGezeigt.add(String(p.id)); break; }
      }
    }

    // Kandidaten-Map: productId -> {product, variant, count (distinct Cart-Items), firstIdx}
    const cand = new Map();
    lists.forEach((list, idx) => {
      const seen = new Set();
      for (const p of list) {
        const v = valid(p);
        if (!v) continue;
        if (schonGezeigt.has(String(p.id))) continue; // hat die Produktseite schon gefragt
        const pid = String(p.id);
        if (seen.has(pid)) continue; // pro Cart-Item nur einmal zaehlen
        seen.add(pid);
        if (!cand.has(pid)) cand.set(pid, { product: p, variant: v, count: 0, firstIdx: idx });
        const c = cand.get(pid);
        c.count += 1;
        if (idx < c.firstIdx) c.firstIdx = idx;
      }
    });
    const all = [...cand.values()];
    if (!all.length) return null;

    // 1) Konsens: kommt bei >=2 Cart-Items vor -> hoechster Count, dann fruehestes Cart-Item
    const consensus = all
      .filter((c) => c.count >= 2)
      .sort((a, b) => b.count - a.count || a.firstIdx - b.firstIdx);
    if (consensus.length) return consensus[0];

    // 2) Fallback: erstes Cart-Item (oben) -> sein erstes gueltiges Complementary
    const firstHit = all
      .filter((c) => c.firstIdx === 0)
      .sort((a, b) => {
        // Reihenfolge wie in der Complementary-Liste des ersten Items
        const order = (lists[0] || []).map((p) => String(p.id));
        return order.indexOf(String(a.product.id)) - order.indexOf(String(b.product.id));
      });
    if (firstHit.length) return firstHit[0];

    // 3) Last resort: irgendein Kandidat (fruehestes Item)
    return all.sort((a, b) => a.firstIdx - b.firstIdx)[0];
  }

  imgUrl(u, w) {
    if (!u) return '';
    return u + (u.includes('?') ? '&' : '?') + 'width=' + w;
  }
  // Betraege kommen aus den Storefront-JSONs bereits in der Kundenwaehrung (Cent). Das Zahlenformat
  // liefert das Theme als Beispiel 1.234,56 in dieser Waehrung (Muster wie assets/shipping-estimator.js).
  moneyFormat() {
    if (this._fmt) return this._fmt;
    const sample = this.dataset.moneySample || '';
    const m = sample.match(/\d[\d.,'’   ]*\d/);
    if (m && m[0].replace(/\D/g, '') === '123456') {
      const num = m[0];
      const f = { pre: sample.slice(0, m.index), suf: sample.slice(m.index + num.length), decSep: num.charAt(num.length - 3), grp: num.slice(0, -3).replace(/\d/g, '').charAt(0) };
      const fmt = (cents) => {
        const s = (cents / 100).toFixed(2);
        let i = s.slice(0, -3);
        if (f.grp) i = i.replace(/\B(?=(\d{3})+(?!\d))/g, f.grp);
        return f.pre + i + f.decSep + s.slice(-2) + f.suf;
      };
      if (fmt(123456) === sample) return (this._fmt = fmt);
    }
    const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    const loc = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'de';
    return (this._fmt = (cents) => {
      try { return new Intl.NumberFormat(loc, { style: 'currency', currency: cur }).format(cents / 100); }
      catch { return (cents / 100).toFixed(2) + ' ' + cur; }
    });
  }
  money(cents) { return this.moneyFormat()(cents); }
  esc(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  // Die ersten zwei Stichpunkte der Produktbeschreibung als reiner Text (Fett-Anfang + Satz), je hoechstens
  // ~180 Zeichen samt Fett-Anfang. Ohne Stichpunktliste: erster Absatz, gekuerzt. Nie HTML aus der Beschreibung uebernehmen.
  kurzinfo(p) {
    try {
      const doc = new DOMParser().parseFromString(String(p.description || ''), 'text/html');
      const kuerze = (s, n) => { s = s.replace(/\s+/g, ' ').trim(); if (s.length <= n) return s; const c = s.slice(0, n); return c.slice(0, Math.max(c.lastIndexOf(' '), n - 20)).replace(/[,;:\s]+$/, '') + ' …'; };
      const ul = doc.querySelector('ul');
      const lis = ul ? [...ul.children].filter((x) => x.tagName === 'LI').slice(0, 2) : [];
      if (lis.length) {
        return '<ul>' + lis.map((li) => {
          const st = li.querySelector('strong');
          const lead = st ? st.textContent.trim() : '';
          const rest = kuerze(li.textContent.replace(lead, ''), Math.max(60, 180 - lead.length));
          return '<li>' + (lead ? '<strong>' + this.esc(lead) + '</strong> ' : '') + this.esc(rest) + '</li>';
        }).join('') + '</ul>';
      }
      const absatz = [...doc.querySelectorAll('p')].map((x) => x.textContent.trim()).find((t) => t.length > 40);
      return absatz ? '<p>' + this.esc(kuerze(absatz, 220)) + '</p>' : '';
    } catch { return ''; }
  }

  cardHtml(pick) {
    const p = pick.product, v = pick.variant;
    const info = this.kurzinfo(p);
    const full = v.price;
    const custom = this.upsellPrices[String(p.id)];
    const disc = Number.isFinite(custom) && custom > 0 && custom < full ? custom : Math.round(full * 0.9);
    const img = p.featured_image
      ? `<img src="${this.imgUrl(p.featured_image, 120)}" alt="" width="60" height="60" loading="lazy">`
      : '';
    return `<div class="cart-upsell__inner">
      <p class="cart-upsell__heading">${this.esc(this.text('heading'))}</p>
      <div class="cart-upsell__list">
        <div class="cart-upsell__card" data-product-id="${p.id}" data-variant-id="${v.id}">
          <a class="cart-upsell__media" href="${p.url}" tabindex="-1" aria-hidden="true">${img}</a>
          <div class="cart-upsell__info">
            <a class="cart-upsell__title" href="${p.url}">${this.esc(p.title)}</a>
            <span class="cart-upsell__price">
              <span class="cart-upsell__price-new">${this.money(disc)}</span>
              <s class="cart-upsell__price-old">${this.money(full)}</s>
            </span>
            ${info ? `<button type="button" class="cart-upsell__info-link" aria-expanded="false">${this.esc(this.text('info'))}</button>` : ''}
          </div>
          <button type="button" class="cart-upsell__add" data-variant-id="${v.id}" aria-label="${this.esc(this.text('addLabel', p.title))}">${this.esc(this.text('add'))}</button>
          ${info ? `<div class="cart-upsell__tip">${info}</div>` : ''}
        </div>
      </div>
    </div>`;
  }

  async addToCart(variantId, btn) {
    if (!variantId) return;
    if (btn) btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { _upsell: 'cart-drawer' } }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      const parsed = await res.json();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && parsed.sections) {
        cartDrawer.renderContents(parsed);
      } else {
        window.location.href = `${this.root}cart`;
      }
    } catch (e) {
      console.error('[cart-upsell] add failed', e);
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }
}
customElements.define('cart-upsell', CartUpsell);
